import type {
    Answer,
    ItemQuizQuestion,
    Variation,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/api/creatorElements.api.ts';
import {
    QUIZ_CONTENT_TYPES,
    REQUIRED_ANSWER_TYPES,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/questions/ui/creator/constants.ts';
import { DEFAULT_TIME_IN_SECONDS_PER_QUESTION } from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/questions/ui/filters/constants.ts';
import { ELEMENTS_STATUSES } from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/tables/elements-table/constants.ts';
import type { CaretCsvDefinition } from '@shared/csv/use-caret-utils';
import { createCaretCsvHandler, createCsvFieldHelpers, normalizeCaretCsvField } from '@shared/csv/use-caret-utils';

import {
    ACCENT_INSENSITIVE_COMPARE,
    ANSWER_SLOTS,
    DECIMAL_RADIX,
    DEFAULT_MINIMUM_CORRECT_ANSWERS,
    DEFAULT_TOTAL_ANSWERS_TO_SELECT,
    FINAL_QUIZ_TYPE,
    PRACTICE_QUIZ_TYPE,
    QUESTIONS_CSV_FIELD,
    QUESTIONS_CSV_HEADER,
} from './constants';
import {
    getCsvToRequiredAnswerType,
    parseAnswersFromCsvFields,
    validateQuestionCsvRow,
} from './questionsCsvValidation';

const { formatField, isEmpty: isCsvEmpty } = createCsvFieldHelpers();

const hasCsvQuestionId = (questionId: string | undefined) => {
    const trimmed = questionId?.trim() || '';
    return Boolean(trimmed && !isCsvEmpty(trimmed));
};

const parseCsvQuestionId = (questionId: string | undefined): number | null => {
    const trimmed = normalizeCaretCsvField(questionId);
    if (!hasCsvQuestionId(trimmed)) {
        return null;
    }

    const parsedId = Number.parseInt(trimmed, DECIMAL_RADIX);
    return Number.isNaN(parsedId) ? null : parsedId;
};

const questionIdExistsIn = (questionId: number, existing: ItemQuizQuestion[]) =>
    existing.some((question) => question.id !== null && Number(question.id) === questionId);

const isQuestionUpdate = (candidate: ItemQuizQuestion, existing: ItemQuizQuestion[]) =>
    candidate.id !== null && questionIdExistsIn(candidate.id, existing);

const isSameText = (left: string, right: string) =>
    left.localeCompare(right, undefined, ACCENT_INSENSITIVE_COMPARE) === 0;

const hasVariationIn = (variation: Variation, candidateVariations: Variation[]) =>
    candidateVariations.some((candidate) => isSameText(candidate.quizContent, variation.quizContent));

const allVariationsExistIn = (variations: Variation[], candidateVariations: Variation[]) =>
    variations.every((variation) => hasVariationIn(variation, candidateVariations));

const hasAnswerIn = (answer: Answer, candidateAnswers: Answer[]) =>
    candidateAnswers.some((candidate) => isSameText(candidate.text, answer.text));

const allAnswersExistIn = (answers: Answer[], candidateAnswers: Answer[]) =>
    answers.every((answer) => hasAnswerIn(answer, candidateAnswers));

const isSameQuestionContent = (existing: ItemQuizQuestion, candidate: ItemQuizQuestion) =>
    existing.isFinalQuizQuestion === candidate.isFinalQuizQuestion &&
    allVariationsExistIn(existing.quizQuestionVariations, candidate.quizQuestionVariations) &&
    allAnswersExistIn(existing.quizAnswers, candidate.quizAnswers);

const isDuplicateQuestion = (existing: ItemQuizQuestion[], candidate: ItemQuizQuestion) =>
    existing.some((question) => isSameQuestionContent(question, candidate));

const formatAnswerSlots = (answers: string[]) => {
    const slots: string[] = [];
    for (let i = 0; i < ANSWER_SLOTS; i++) {
        slots.push(formatField(answers[i]));
    }
    return slots;
};

const parseQuestionRow = (fields: string[]): ItemQuizQuestion | null => {
    const quizType = fields[QUESTIONS_CSV_FIELD.quizType] ?? '';
    const variations = [
        fields[QUESTIONS_CSV_FIELD.variation1],
        fields[QUESTIONS_CSV_FIELD.variation2],
        fields[QUESTIONS_CSV_FIELD.variation3],
    ]
        .filter((v) => v && !isCsvEmpty(v))
        .map((quizContent) => ({
            quizContent,
            quizContentType: QUIZ_CONTENT_TYPES.TEXT,
        }));

    const answers = parseAnswersFromCsvFields(fields);

    const correctFeedback = fields[QUESTIONS_CSV_FIELD.correctFeedback];
    const partiallyCorrectFeedback = fields[QUESTIONS_CSV_FIELD.partiallyCorrectFeedback];
    const wrongFeedback = fields[QUESTIONS_CSV_FIELD.wrongFeedback];
    const questionIntroduction = fields[QUESTIONS_CSV_FIELD.questionIntroduction];
    const questionPrompt = fields[QUESTIONS_CSV_FIELD.questionPrompt];
    const typeOfRequiredAnswer = fields[QUESTIONS_CSV_FIELD.typeOfRequiredAnswer] ?? REQUIRED_ANSWER_TYPES.CORRECT;
    const minimumCorrectAnswersToSelect = Number.parseInt(
        fields[QUESTIONS_CSV_FIELD.minimumCorrectAnswersToSelect] ?? String(DEFAULT_MINIMUM_CORRECT_ANSWERS),
        DECIMAL_RADIX,
    );
    const totalAnswersToSelect = Number.parseInt(
        fields[QUESTIONS_CSV_FIELD.totalAnswersToSelect] ?? String(DEFAULT_TOTAL_ANSWERS_TO_SELECT),
        DECIMAL_RADIX,
    );

    return {
        id: parseCsvQuestionId(fields[QUESTIONS_CSV_FIELD.questionId]),
        adminQuestionId: null,
        quizAnswers: answers,
        quizQuestionVariations: variations,
        introText: isCsvEmpty(questionIntroduction) ? '' : questionIntroduction,
        outroText: isCsvEmpty(questionPrompt) ? null : questionPrompt,
        isFinalQuizQuestion: isSameText(quizType, FINAL_QUIZ_TYPE),
        totalAnswersToSelect: Number.isNaN(totalAnswersToSelect)
            ? DEFAULT_TOTAL_ANSWERS_TO_SELECT
            : totalAnswersToSelect,
        minimumCorrectAnswersToSelect: Number.isNaN(minimumCorrectAnswersToSelect)
            ? DEFAULT_MINIMUM_CORRECT_ANSWERS
            : minimumCorrectAnswersToSelect,
        requiredAnswerType: getCsvToRequiredAnswerType(typeOfRequiredAnswer),
        correctFeedback: isCsvEmpty(correctFeedback) ? null : correctFeedback,
        partiallyCorrectFeedback: isCsvEmpty(partiallyCorrectFeedback) ? null : partiallyCorrectFeedback,
        wrongFeedback: isCsvEmpty(wrongFeedback) ? null : wrongFeedback,
        status: ELEMENTS_STATUSES.DRAFT_STATUS,
        isActive: true,
    };
};

const formatQuestionRow = (question: ItemQuizQuestion): string[] => {
    const variations = question.quizQuestionVariations.map((v) => v.quizContent);
    const correctAnswers = question.quizAnswers.filter((a) => a.isCorrect).map((a) => a.text);
    const wrongAnswers = question.quizAnswers.filter((a) => !a.isCorrect).map((a) => a.text);

    return [
        question.id?.toString() ?? formatField(null),
        question.isFinalQuizQuestion ? FINAL_QUIZ_TYPE : PRACTICE_QUIZ_TYPE,
        formatField(variations[0]),
        formatField(variations[1]),
        formatField(variations[2]),
        ...formatAnswerSlots(correctAnswers),
        ...formatAnswerSlots(wrongAnswers),
        formatField(question.correctFeedback),
        formatField(question.partiallyCorrectFeedback),
        formatField(question.wrongFeedback),
        formatField(question.introText),
        formatField(question.outroText),
        DEFAULT_TIME_IN_SECONDS_PER_QUESTION.toString(),
        getCsvToRequiredAnswerType(question.requiredAnswerType),
        String(question.minimumCorrectAnswersToSelect ?? DEFAULT_MINIMUM_CORRECT_ANSWERS),
        String(question.totalAnswersToSelect),
    ];
};

/** Questions-specific CSV schema and row mapping */
export const questionsCsvDefinition: CaretCsvDefinition<ItemQuizQuestion> = {
    header: QUESTIONS_CSV_HEADER,
    validateRow: validateQuestionCsvRow,
    parseRow: parseQuestionRow,
    formatRow: formatQuestionRow,
    isDuplicate: (existing, candidate) =>
        isQuestionUpdate(candidate, existing) ? false : isDuplicateQuestion(existing, candidate),
    templateFileName: 'questions-template.csv',
    exportFileName: 'questions.csv',
    issuesFileName: 'questions-import-errors.csv',
};

export const questionsCsvHandler = createCaretCsvHandler(questionsCsvDefinition);
